/**
 * PINE → D:\pine\frontend\src\components\Modals\workstation\GenerateDeckModal.jsx
 *
 * Trimmed to the one thing that actually needs a human before the build runs: confirming
 * which statuses this deck should cover (skill's Step 1b). Everything else (dates, cadence,
 * interviewers, reanchor flag) now takes sane defaults in the controller. No polling — the
 * deck is emailed when ready, so this modal's job is just to start the job and get out of
 * the way.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import toastr from "../../../utils/toast";

export default function GenerateDeckModal({
  role_id,
  role_name,
  token,
  onClose,
}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [phrasingMap, setPhrasingMap] = useState({});
  const [checked, setChecked] = useState([]);
  const [starting, setStarting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [dryRunMode, setDryRunMode] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/workstation/status_update_statuses/${role_id}`,
          auth,
        );
        const list = res.data?.statuses || [];
        setDryRunMode(res.data?.dry_run === true);
        setStatuses(list);
        setPhrasingMap(res.data?.phrasing_map || {});
        setChecked(list.map((s) => s.major_status).filter(Boolean));
      } catch (err) {
        console.error("Failed to load statuses", err);
        toastr.error("Could not load statuses for this role");
      } finally {
        setLoadingStatuses(false);
      }
    })();
  }, [role_id]);

  const handleGenerate = async () => {
    if (!checked.length) {
      toastr.warning("Select at least one status for the deck to cover");
      return;
    }
    setStarting(true);
    try {
      const dryRun = dryRunMode === true;
      const res = await axios.post(
        `${API_BASE_URL}/workstation/generate_status_update`,
        {
          role_id,
          dry_run: dryRun,
          intake: {
            roles: role_name ? [role_name] : [],
            update_date: new Date().toISOString().slice(0, 10),
          },
          status_coverage: {
            included_statuses: checked,
            local_label_meanings: {},
          },
          phrasing_map: phrasingMap,
        },
        auth,
      );
      if (dryRunMode && res.data?.dry_run) {
        setEstimate(res.data.estimate || null);
        return;
      }
      if (res.data?.dry_run) {
        setEstimate(res.data.estimate || null);
        setDryRunMode(true);
        toastr.warning("Dry-run mode is enforced by the server; no deck was generated.");
        return;
      }
      const { candidate_count, emailing_to } = res.data;
      toastr.success(
        emailing_to
          ? `Building deck for ${candidate_count} candidates. We'll email it to ${emailing_to} shortly.`
          : `Building deck for ${candidate_count} candidates.`,
      );
      onClose();
    } catch (err) {
      console.error(err);
      toastr.error(err.response?.data?.message || "Could not start the deck build");
    } finally {
      setStarting(false);
    }
  };
  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,.5)" }}
    >
      <div className="modal-dialog modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Generate Status Update Deck</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <label className="form-label fw-bold">
              Statuses this deck covers
            </label>
            {loadingStatuses ? (
              <div className="py-2">
                <CircularProgress size={18} />
              </div>
            ) : (
              <div className="row">
                {statuses.map((s) => (
                  <div
                    className="col-md-6"
                    key={s.master_status_id || s.major_status}
                  >
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`st-${s.master_status_id}`}
                        checked={checked.includes(s.major_status)}
                        onChange={(e) => {
                          setChecked((prev) =>
                            e.target.checked
                              ? [...prev, s.major_status]
                              : prev.filter((x) => x !== s.major_status),
                          );
                          setEstimate(null);
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`st-${s.master_status_id}`}
                      >
                        {s.major_status || "(no status)"}{" "}
                        <span className="text-muted">
                          ({s.candidate_count})
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 text-muted">
              <small className="text-muted">
                The deck layout is chosen automatically from the candidate data.
              </small>
            </div>
          </div>

          {dryRunMode && estimate && (
            <div className="mx-3 mb-3 alert alert-info">
              <strong>Preflight estimate</strong>
              <div>Candidates: {estimate.candidate_count} · GPT calls: {estimate.planned_api_calls}</div>
              <div>Movement baseline: {estimate.comparison_date || "none (first successful deck for this role)"}</div>
              <div>Estimated tokens: {Number(estimate.estimated_tokens?.input || 0).toLocaleString()} input + {Number(estimate.estimated_tokens?.output || 0).toLocaleString()} output</div>
              <div>Estimated cost: {estimate.cost_usd?.total == null ? "not configured (set OPENAI_INPUT_PRICE_PER_1M and OPENAI_OUTPUT_PRICE_PER_1M)" : `$${Number(estimate.cost_usd.total).toFixed(4)} USD`}</div>
              <small>Approximate only; the estimate step made no GPT calls and wrote no database job.</small>
            </div>
          )}
          <div className="modal-footer">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleGenerate}
              disabled={starting || loadingStatuses || dryRunMode === null}
            >
              {starting
                ? dryRunMode ? "Estimating…" : "Starting…"
                : dryRunMode
                  ? estimate ? "Re-run Preflight Estimate" : "Run Preflight Estimate"
                  : "Generate Deck"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
